import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import {
  Attachment,
  TicketResponse as Response,
  Ticket,
} from "../../../services/swagger";

import Authorized from "../../atoms/Authorized";
import useRefresh from "../../../hooks/useRefresh";
import { OpenAiService, TicketService } from "../../../services/api";
import useConfirm from "../../../hooks/useConfirm";
import { useEffect, useState } from "react";
import FormInput from "../../molecules/FormInput";
import DateAndName from "../../molecules/DateAndName";
import AttachmentList from "../../molecules/AttachmentList";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import AskAiDialog from "../../molecules/AskAiDialog";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import useDidMountEffect from "../../../hooks/useDidMountEffect";
import TextBody from "../../atoms/TextBody";
import OptionsButton from "../../molecules/OptionsButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import GTranslateOutlinedIcon from "@mui/icons-material/GTranslateOutlined";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import { useGlobalContext } from "../../../stores/Global";
import LockIcon from "@mui/icons-material/Lock";
import useRole from "../../../hooks/useRole";
import { AntSwitch } from "../../atoms/AntSwitch";
import { useMutation } from "@tanstack/react-query";
import LoadingTextBody from "../../atoms/LoadingTextBody";

interface TicketResponseProps {
  ticket: Ticket;
  response: Response;
}

export default function TicketResponse(props: TicketResponseProps) {
  const { t } = useTranslation("Ticket");
  const { response } = props;
  const [editMode, setEditMode] = useState(false);
  const [aiResponseMode, setAiResponseMode] = useState(false);
  const [aiResponseText, setAiResponseText] = useState("");
  const [internalResponse, setInternalResponse] = useState<boolean>(
    () => response.isInternal ?? false
  );
  const [content, setContent] = useState<string>(response.content || "");
  const [openAskAIDialog, setOpenAskAIDialog] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const refresh = useRefresh();
  const confirm = useConfirm();
  const { context } = useGlobalContext();
  const checkRole = useRole();

  useEffect(() => {
    if (!response) return;
    const newAttachments: Attachment[] = [];
    for (const attachment of response.attachments) {
      if (attachment.name) {
        newAttachments.push(attachment);
      } else {
        const obj = attachment as unknown as string[];
        let name = "";
        for (let i = 0; i < Object.keys(obj).length; i++) {
          name += obj[i];
        }
        const type = "image";
        const path = `/tickets/${props.ticket.id}/${name}`;
        newAttachments.push({
          name,
          type,
          path,
        });
      }
    }
    setAttachments(newAttachments);
  }, [response]);

  useDidMountEffect(() => {
    if (editMode) setContent(response?.content || "");
  }, [editMode]);

  const handleResponseClose = () => {
    setAiResponseMode(false);
  };

  const { mutate: handleTranslate, isPending: isTranslationPending } =
    useMutation({
      mutationFn: async () => {
        setAiResponseMode(true);
        const res = await OpenAiService.createTranslatePrompt({
          createTranslateBody: {
            message: content,
          },
        });

        return res?.data?.data;
      },
      onSuccess: (data) => {
        setAiResponseText(data || "");
      },
    });

  const { mutate: handleSummarize, isPending: isSummarizePending } =
    useMutation({
      mutationFn: async () => {
        setAiResponseMode(true);
        return await OpenAiService.createSummarizePrompt({
          createSummarizeBody: {
            message: content,
          },
        });
      },
      onSuccess: (data) => {
        setAiResponseText(data.data?.data || "");
      },
    });

  const handleAskAIOpen = () => {
    setOpenAskAIDialog(true);
  };

  const handleDialogClose = () => {
    setOpenAskAIDialog(false);
  };

  const handleClickSave = () => {
    confirm(() => handleUpdateResponse(internalResponse));
  };

  const handleUpdateResponse = async (newValue: boolean) => {
    if (!response._id || !props.ticket?._id) return;
    if (response.attachments.length === 0 && !content)
      return toast(t("ResponseEmpty"), { type: "warning" });
    const res = await TicketService.updateTicketResponse({
      responseId: response._id,
      ticketId: props.ticket._id,
      updateTicketResponseBody: {
        content,
        internalResponse: newValue,
      },
    });
    setEditMode(false);
    setAiResponseMode(false);
    if (res?.data?.success) refresh();
  };

  const handleSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    confirm(async () => {
      setInternalResponse(newValue);
      await handleUpdateResponse(newValue);
    });
  };

  const handleClickDelete = () => {
    confirm(handleDelete);
  };
  const handleDelete = async () => {
    if (!response._id || !props.ticket?._id) return;
    const res = await TicketService.deleteTicketResponse({
      responseId: response._id,
      ticketId: props.ticket._id,
    });
    if (res?.data?.success) refresh();
  };

  return (
    <Box>
      <Box
        // elevation={response.isInternal ? 0 : 2}
        sx={{
          pt: 3,
          pl: 2,
          pr: 2,
          pb: 1,

          bgcolor: (theme) => theme.palette.background.default,
          position: "relative",
        }}
      >
        {response.isInternal && (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 1,
              display: "flex",
              color: (theme) => theme.palette.primary.main,
              fontSize: 12,
            }}
          >
            <LockIcon sx={{ fontSize: 12, mr: 1 }} />
          </Box>
        )}

        <Stack spacing={1}>
          <DateAndName
            date={response.date}
            name={response.user?.name || (response as any).name}
          />
          {editMode ? (
            <FormInput
              field="content"
              value={content}
              onChange={(v) => setContent(v as string)}
              multiline
              label={""}
              type="string"
              minRows={4}
            />
          ) : (
            !aiResponseMode && <TextBody>{response.content}</TextBody>
          )}

          {isTranslationPending || isSummarizePending ? (
            <LoadingTextBody />
          ) : (
            aiResponseMode && (
              <Typography whiteSpace="pre-wrap">{aiResponseText}</Typography>
            )
          )}

          {attachments && (
            <AttachmentList
              ticketId={props.ticket.id.toString()}
              attachments={attachments}
            />
          )}

          <Authorized role="agent">
            <Stack direction={"row"} spacing={"auto"}>
              <FormControlLabel
                control={
                  <AntSwitch
                    sx={{ mr: 1 }}
                    inputProps={{ "aria-label": "ant design" }}
                    checked={internalResponse}
                    onChange={handleSwitchChange}
                    color="primary"
                    size="small"
                  />
                }
                label={t("PrivateResponse")}
              />

              <Stack spacing={1} direction={"row"} alignItems={"center"}>
                {editMode ? (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      onClick={handleClickSave}
                    >
                      {t("Save")}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => setEditMode(false)}
                    >
                      {t("Cancel")}
                    </Button>
                  </>
                ) : (
                  <>
                    <AskAiDialog
                      open={openAskAIDialog}
                      onClose={handleDialogClose}
                      defaultContent={content}
                    />

                    {aiResponseMode && !isTranslationPending && (
                      <Button
                        onClick={handleResponseClose}
                        size="small"
                        startIcon={<GTranslateOutlinedIcon />}
                      >
                        {t("ShowOriginal")}
                      </Button>
                    )}
                    <OptionsButton
                      options={[
                        {
                          icon: <EditIcon color="primary" />,
                          label: t("Edit"),
                          onClick: () => setEditMode(true),
                          disabled: response.user?._id !== context.user?._id,
                        },
                        {
                          icon: <GTranslateOutlinedIcon color="primary" />,
                          label: t("Translate"),
                          onClick: handleTranslate,
                        },
                        {
                          icon: <TextSnippetIcon color="primary" />,
                          label: t("Summarize"),
                          onClick: handleSummarize,
                        },
                        {
                          icon: <AutoAwesomeOutlinedIcon color="primary" />,
                          label: t("AskAI"),
                          onClick: handleAskAIOpen,
                        },
                        {
                          divider: true,
                          role: "agent",
                          hidden:
                            response.user?._id !== context.user?._id &&
                            !checkRole("admin"),
                        },
                        {
                          icon: <DeleteIcon color="error" />,
                          label: t("Delete"),
                          onClick: handleClickDelete,
                          role: "agent",
                          hidden:
                            response.user?._id !== context.user?._id &&
                            !checkRole("admin"),
                        },
                      ]}
                    />
                  </>
                )}
              </Stack>
            </Stack>
          </Authorized>
        </Stack>
      </Box>
      <Divider />
    </Box>
  );
}
